/**
 * Audit Log API Service
 *
 * Client for the configuration audit trail API (User Story 4 - 007-transferhub-refinements)
 */

import { BaseApiService, unwrapApiResponse } from './base';
import { snakeToCamel } from '../../utils/caseTransform';
import type {
  AuditLogEntry,
  AuditLogSearchResponse,
  EntityAuditHistoryResponse,
  AuditLogSearchParams,
  AuditEntityType,
} from '../../types/auditLog';

/**
 * Transform API response to match frontend camelCase conventions using generic converter
 */
function transformAuditLog(log: Record<string, unknown>): AuditLogEntry {
  const entry = snakeToCamel<any>(log);
  return {
    auditId: entry.auditId,
    timestamp: entry.timestamp,
    entityType: entry.entityType as AuditEntityType,
    entityId: entry.entityId,
    action: entry.action as AuditLogEntry['action'],
    userId: entry.userId,
    previousValue: entry.previousValue,
    newValue: entry.newValue,
    changeDetails: entry.changeDetails,
  };
}

/**
 * Build query string from search params
 */
function buildAuditQueryString(params: AuditLogSearchParams): string {
  const queryParts: string[] = [];

  if (params.entityType) {
    queryParts.push(`entity_type=${encodeURIComponent(params.entityType)}`);
  }
  if (params.entityId) {
    queryParts.push(`entity_id=${encodeURIComponent(params.entityId)}`);
  }
  if (params.userId) {
    queryParts.push(`user_id=${encodeURIComponent(params.userId)}`);
  }
  if (params.action) {
    queryParts.push(`action=${encodeURIComponent(params.action)}`);
  }
  if (params.startDate) {
    queryParts.push(`start_date=${encodeURIComponent(params.startDate)}`);
  }
  if (params.endDate) {
    queryParts.push(`end_date=${encodeURIComponent(params.endDate)}`);
  }
  if (params.limit) {
    queryParts.push(`limit=${params.limit}`);
  }
  if (params.nextToken) {
    queryParts.push(`next_token=${encodeURIComponent(params.nextToken)}`);
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

/**
 * Audit Log API Service
 */
class AuditLogApiService extends BaseApiService {
  /**
   * Search audit logs with filters
   *
   * @param params - Search parameters
   * @returns Paginated list of audit log entries
   */
  async searchAuditLogs(params: AuditLogSearchParams = {}): Promise<AuditLogSearchResponse> {
    const queryString = buildAuditQueryString(params);
    const response = await this.get(`/audit-logs${queryString}`);

    // Handle both direct response and response.json() patterns
    const raw = response.json ? await response.json() : response;
    const data = unwrapApiResponse(raw);

    const camelData = snakeToCamel<any>(data);
    return {
      auditLogs: (camelData.auditLogs || []).map(transformAuditLog),
      nextToken: camelData.nextToken || null,
      totalCount: camelData.totalCount,
    };
  }

  /**
   * Get complete audit history for a specific entity
   *
   * @param entityType - Type of entity (partner, connection, task)
   * @param entityId - ID of the entity
   * @returns Complete audit history with summary
   */
  async getEntityAuditHistory(
    entityType: AuditEntityType,
    entityId: string
  ): Promise<EntityAuditHistoryResponse> {
    const response = await this.get(`/audit-logs/${entityType}/${entityId}`);

    // Handle both direct response and response.json() patterns
    const raw = response.json ? await response.json() : response;
    const data = unwrapApiResponse(raw);

    const camelData = snakeToCamel<any>(data);
    return {
      entityType: (camelData.entityType || entityType) as AuditEntityType,
      entityId: camelData.entityId || entityId,
      auditLogs: (camelData.auditLogs || []).map(transformAuditLog),
      summary: camelData.summary,
      totalCount: camelData.totalCount,
    };
  }

  /**
   * Get recent audit logs for all entities (for dashboard/global view)
   *
   * @param limit - Maximum number of entries to return
   * @returns Recent audit log entries
   */
  async getRecentAuditLogs(limit = 20): Promise<AuditLogEntry[]> {
    const response = await this.searchAuditLogs({ limit });
    return response.auditLogs;
  }

  /**
   * Get audit logs for a specific user
   *
   * @param userId - User ID to filter by
   * @param params - Additional search parameters
   * @returns Audit log entries by the user
   */
  async getAuditLogsByUser(
    userId: string,
    params: Omit<AuditLogSearchParams, 'userId'> = {}
  ): Promise<AuditLogSearchResponse> {
    return this.searchAuditLogs({ ...params, userId });
  }
}

// Create and export a singleton instance
export const auditLogApi = new AuditLogApiService();

export default auditLogApi;
